import axios from "axios";
import useUserStore from "../context/useUserStore";
import storage from "../utils/storage";
import { attemptRefresh, clearSession, redirectToLogin } from "./session";

/**
 * SWR fetcher — plain axios GET for use with useSWR(key, fetcher).
 *
 * Prepends /api to the key since SWR keys in constants.js are relative paths
 * (e.g., "/menu") but this fetcher uses plain axios without a baseURL.
 * The useApi hook has baseURL = "/api" baked in, but this standalone
 * fetcher does not.
 *
 * Use this for PUBLIC endpoints only (menu/vehicles/events) — they require
 * no auth. Admin reads must use `authFetcher` (DEF-010).
 *
 * @param {string} url - Relative path from constants, e.g. "/menu"
 * @returns {Promise<any>} Response data
 */
export const fetcher = (url) =>
  axios.get(`/api${url}`, { withCredentials: true }).then((res) => res.data);

/**
 * authFetcher — SWR fetcher for AUTHENTICATED endpoints (admin reads, DEF-010).
 *
 * Injects the Bearer token the same way the `useApi` request interceptor does:
 * read the token from the Zustand store (memory), falling back to storage. The
 * SWR `(url) => data` signature and `/api` prefix match the public `fetcher`,
 * and `withCredentials: true` keeps the httpOnly refresh cookie flowing.
 *
 * 401 recovery (DEF-015): on a 401 from an expired token, attempt ONE silent
 * refresh (`POST /api/auth/refresh`) and retry the read once. If the refresh
 * fails, clear the session and redirect to `/login` carrying the current page
 * (`state.from`) so re-login returns the user there. This mirrors the SPR-007
 * `useApi` mutation flow via the shared `session.js` helpers (no drift), and
 * guards against refresh loops (a single retry, no recursion).
 *
 * @param {string} url - Relative path from constants, e.g. "/admin/categories"
 * @returns {Promise<any>} Response data
 */
const bearerHeaders = () => {
  const token =
    useUserStore.getState().accessToken ?? storage.getItem(storage.ITEMS.jwt);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const authFetcher = async (url) => {
  try {
    const res = await axios.get(`/api${url}`, {
      withCredentials: true,
      headers: bearerHeaders(),
    });
    return res.data;
  } catch (err) {
    if (err?.response?.status !== 401) throw err;

    // Expired token — one silent refresh, then retry the read once.
    const newToken = await attemptRefresh();
    if (newToken) {
      const retry = await axios.get(`/api${url}`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${newToken}` },
      });
      return retry.data;
    }

    // Refresh failed — clear session and route to /login (return-to-page).
    clearSession();
    redirectToLogin();
    throw err;
  }
};
